import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";

const STORAGE_KEY = "vexamos.cookie_consent.v1";

/**
 * Minimal cookie / storage consent banner. We store only a session token to
 * keep users signed in and don't run third-party ad-tracking, so the wording
 * is minimal — but a visible notice + acknowledgement is still required for
 * EU visitors under GDPR.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay a bit so the banner doesn't flash on hard reload
    const t = setTimeout(() => {
      try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (!v) setVisible(true);
      } catch {
        setVisible(true);
      }
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const accept = (value) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: value, ts: Date.now() })); } catch {}
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 bg-zinc-950 text-zinc-100 border border-zinc-800 shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-semibold">Cookies &amp; storage</div>
          <button
            onClick={() => accept("dismissed")}
            className="text-zinc-400 hover:text-white -m-1 p-1"
            aria-label="Dismiss"
            data-testid="cookie-banner-dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <p className="text-xs leading-relaxed text-zinc-300">
          We store a secure session token to keep you signed in and no third-party
          advertising cookies. Read our{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-white" data-testid="cookie-banner-privacy-link">
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => accept("accepted")}
            className="bg-amber-400 text-zinc-950 px-3 py-1.5 text-xs font-bold hover:bg-amber-300"
            data-testid="cookie-banner-accept"
          >
            Got it
          </button>
          <Link
            to="/privacy"
            className="text-xs text-zinc-400 hover:text-white underline underline-offset-2"
          >
            Learn more
          </Link>
        </div>
      </div>
    </div>
  );
}
